import { Col, Form, Input, Modal, Row, Upload } from "antd";
import { RcFile } from "antd/lib/upload";
import { UploadFile } from "antd/lib/upload/interface";
import { useTranslation } from "react-i18next";
import { useAddBugReportAttachments, useCreateUserReportedBugReport } from "../../../../common/api/queries";
import ActionButton from "../../../../common/components/buttons/ActionButton";
import { FileUploadAccept, ModalSizes, rowGutter } from "../../../../common/constants";
import { setErrorsToForm } from "../../../../common/utils/formUtils";
import { validations } from "../../../../common/utils/validationUtils";
import { CreateUserReportedBug } from "../../types";

interface Props {
  open: boolean;
  onFormCancel: () => void;
}

const colSpan = 24;

export const CreateUserBugReportForm = ({ open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUserReportedBug & { files?: RcFile[] }>();

  const createUserReportedBugReport = useCreateUserReportedBugReport();
  const addBugReportAttachments = useAddBugReportAttachments();

  const handleFormSubmit = (): void => {
    form.validateFields().then(async ({ files, ...values }) => {
      const responseData = await createUserReportedBugReport.mutateAsync(values, {
        onSuccess: onFormCancel,
        onError: error => setErrorsToForm(form, "bugReport.attrs", error.violations)
      });

      if (files) {
        const formData = new FormData();
        files?.forEach(file => {
          formData.append("files", file);
        });

        addBugReportAttachments.mutate({ id: responseData.id, object: formData });
      }
    });
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={open}
      title={t("navigation.sideMenu.bugReport")}
      okText={t("common.send")}
      cancelText={t("common.cancel")}
      confirmLoading={createUserReportedBugReport.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      maskClosable={false}
    >
      <Form form={form} layout="vertical" name="createUserBugReportForm">
        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="title"
              label={t("bugReport.attrs.title")}
              rules={[validations.notBlank, validations.size(1, 255)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="content"
              label={t("bugReport.attrs.content")}
              rules={[validations.notBlank, validations.size(1, 255)]}
            >
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="files"
              getValueFromEvent={({ fileList }) =>
                fileList.map((item: UploadFile | File) => (item instanceof File ? item : item.originFileObj))
              }
              rules={[validations.none]}
              valuePropName="fileList"
            >
              <Upload multiple accept={FileUploadAccept.IMAGE} beforeUpload={() => false}>
                <ActionButton icon="plus">{t("common.addAttachments")}</ActionButton>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
